import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { FileText, Globe, MapPin, Search as SearchIcon } from "lucide-react";
import { cn, host, pathOf } from "@/lib/utils";
import { useDebounced } from "@/hooks/useDebounced";
import { ErrorNote } from "@/components/ui";

type SearchResp = {
  sites: { id: string; url: string }[];
  pages: { id: string; url: string; siteId: string }[];
  queries: { query: string; siteId: string; clicks: number }[];
  countries: { country: string; siteId: string; clicks: number }[];
};

type Hit =
  | { kind: "site"; label: string; sub: string; to: string }
  | { kind: "page"; label: string; sub: string; to: string }
  | { kind: "query"; label: string; sub: string; to: string }
  | { kind: "country"; label: string; sub: string; to: string };

export default function GlobalSearch({
  open,
  query,
  onQueryChange,
  onClose,
}: {
  open: boolean;
  query: string;
  onQueryChange: (v: string) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const debounced = useDebounced(query.trim(), 250);
  const key = debounced
    ? `/api/search?q=${encodeURIComponent(debounced)}`
    : null;
  const { data, isLoading, error } = useSWR<SearchResp>(key, {
    // Перебивает глобальный refreshInterval — поиск дёргать только когда вводят.
    refreshInterval: 0,
    dedupingInterval: 30_000,
  });
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
  }, [debounced, open]);

  const hits: Hit[] = [];
  if (data) {
    for (const s of data.sites)
      hits.push({
        kind: "site",
        label: host(s.url),
        sub: s.url,
        to: `/sites/${s.id}`,
      });
    for (const p of data.pages)
      hits.push({
        kind: "page",
        label: pathOf(p.url),
        sub: p.url,
        to: `/sites/${p.siteId}/pages/${p.id}`,
      });
    for (const q of data.queries)
      hits.push({
        kind: "query",
        label: q.query,
        sub: `${q.clicks} кликов`,
        to: `/sites/${q.siteId}`,
      });
    for (const c of data.countries)
      hits.push({
        kind: "country",
        label: c.country.toUpperCase(),
        sub: `${c.clicks} кликов`,
        to: `/sites/${c.siteId}`,
      });
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(hits.length - 1, i + 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
      }
      if (e.key === "Enter") {
        const h = hits[active];
        if (h) {
          navigate(h.to);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hits, active, navigate, onClose]);

  if (!open) return null;

  const groups = [
    { kind: "site" as const, label: "Сайты", icon: Globe },
    { kind: "page" as const, label: "Страницы", icon: FileText },
    { kind: "query" as const, label: "Запросы", icon: SearchIcon },
    { kind: "country" as const, label: "Страны", icon: MapPin },
  ]
    .map((g) => ({ ...g, items: hits.filter((h) => h.kind === g.kind) }))
    .filter((g) => g.items.length);

  let cursor = 0;
  const next = () => cursor++;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-16 sm:pt-24 px-4 bg-ink-950/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Поиск"
        className="w-full max-w-2xl rounded-2xl border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 shadow-card overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 border-b border-ink-100 dark:border-ink-800">
          <SearchIcon className="h-4 w-4 text-ink-400" />
          <input
            autoFocus
            aria-label="Поиск"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Сайт, страница, запрос, страна…"
            className="flex-1 bg-transparent border-0 outline-none py-3 text-sm placeholder:text-ink-400"
          />
          {isLoading && debounced && (
            <span className="text-[11px] text-ink-400">загрузка…</span>
          )}
          <kbd className="text-[11px] text-ink-400 border border-ink-200 dark:border-ink-700 rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>
        <div className="max-h-[50vh] sm:max-h-[60vh] overflow-auto p-2 text-sm">
          {!debounced ? (
            <div className="px-3 py-4 text-ink-500 dark:text-ink-400">
              Начните вводить — поиск идёт по доменам, страницам, запросам и
              странам.
            </div>
          ) : !data && isLoading ? (
            <div className="px-3 py-4 text-ink-500 dark:text-ink-400">
              Подождите…
            </div>
          ) : error ? (
            <ErrorNote className="px-3 py-4">
              Ошибка поиска — попробуйте ещё раз.
            </ErrorNote>
          ) : hits.length === 0 ? (
            <div className="px-3 py-4 text-ink-500 dark:text-ink-400">
              Ничего не найдено по «{debounced}»
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.kind} className="mb-2">
                <div className="px-2 py-1 text-[11px] font-semibold text-ink-400">
                  {g.label}
                </div>
                {g.items.map((h) => {
                  const idx = next();
                  const Icon = g.icon;
                  return (
                    <Link
                      key={`${h.kind}-${idx}-${h.label}`}
                      to={h.to}
                      onClick={onClose}
                      onMouseEnter={() => setActive(idx)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors",
                        idx === active
                          ? "bg-brand-600/10 text-brand-700 dark:text-brand-300"
                          : "hover:bg-ink-50 dark:hover:bg-ink-800/50",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{h.label}</div>
                        <div className="truncate text-xs text-ink-500 dark:text-ink-400">
                          {h.sub}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="border-t border-ink-100 dark:border-ink-800 px-3 py-1.5 text-[11px] text-ink-500 dark:text-ink-400 flex items-center gap-3">
          <span>
            <kbd className="border rounded px-1">↑</kbd>{" "}
            <kbd className="border rounded px-1">↓</kbd> навигация
          </span>
          <span>
            <kbd className="border rounded px-1">Enter</kbd> открыть
          </span>
          <span className="ml-auto">
            <kbd className="border rounded px-1">⌘K</kbd> вызов
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
